import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SigningStatus } from "@/shared/types";
import { useAuth } from "@/shared/hooks";
import { usePagination } from "@/shared/hooks";
import type { DateRange, Locale } from "@/shared/utils";
import { getUniqueSigningStatuses } from "../shared/utils";
import type { StatusOption } from "../shared/utils";
import { useGetAllLegalDocumentsQuery } from "../api";
import { LegalDocumentType } from "../types/documentTypes";
import { SIGNING_STATUS } from "../pages/consts";

export interface DocumentTypeOption {
  label: string;
  value: LegalDocumentType | null;
}

function parseDate(value: string | null): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function normalizeDateToStartOfDay(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

function normalizeDateToEndOfDay(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(23, 59, 59, 999);
  return normalized;
}

function filterBySearch(doc: { id?: number }, searchTerm: string): boolean {
  if (!searchTerm) return true;
  const docId = String(doc.id ?? "");
  return docId.includes(searchTerm);
}

function filterByType(item: { type: LegalDocumentType }, filter: LegalDocumentType | null): boolean {
  return !filter || item.type === filter;
}

function filterByStatus(doc: { signing_status: SigningStatus | null }, filter: string | null): boolean {
  if (!filter) return true;
  if (filter === "null") {
    return doc.signing_status === SIGNING_STATUS.DRAFT;
  }
  return doc.signing_status === Number(filter);
}

function filterByDate(createdAt: string, dateRange: DateRange): boolean {
  const createdDate = parseDate(createdAt);
  if (!createdDate) {
    return !dateRange.start && !dateRange.end;
  }
  if (dateRange.start && createdDate < normalizeDateToStartOfDay(dateRange.start)) {
    return false;
  }
  if (dateRange.end && createdDate > normalizeDateToEndOfDay(dateRange.end)) {
    return false;
  }
  return true;
}

function sortDocumentsByDate(a: { doc: { created_at: string } }, b: { doc: { created_at: string } }): number {
  const dateA = new Date(a.doc.created_at).getTime();
  const dateB = new Date(b.doc.created_at).getTime();
  return dateB - dateA;
}

export function useLegalApplicationsListPage() {
  const { hasRole } = useAuth();
  const { t, i18n } = useTranslation("LegalApplicationsPage");
  const { t: tTemplates } = useTranslation("LegalTemplatesPage");
  const { t: tCommon } = useTranslation("Common");
  const locale = i18n.language as Locale;
  const readOnly = hasRole("accountant");
  const signingRestricted = hasRole("lawyer");

  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });
  const [documentTypeFilter, setDocumentTypeFilter] = useState<LegalDocumentType | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const { data, isLoading, isError } = useGetAllLegalDocumentsQuery();

  const documentTypeOptions: DocumentTypeOption[] = useMemo(() => {
    return [
      { label: t("filters.allDocumentTypes"), value: null },
      { label: tTemplates("templates.vehicleRent.title"), value: LegalDocumentType.VEHICLE_RENTAL },
      { label: tTemplates("templates.commercialPremiseRent.title"), value: LegalDocumentType.PREMISES_LEASE },
      { label: tTemplates("templates.serviceContract.title"), value: LegalDocumentType.SERVICE_AGREEMENT_INDIVIDUAL },
      { label: tTemplates("templates.paidService.title"), value: LegalDocumentType.SERVICE_AGREEMENT_MSB },
      { label: tTemplates("templates.supplyContract.title"), value: LegalDocumentType.GOODS_SUPPLY },
      { label: tTemplates("templates.platformAccess.title"), value: LegalDocumentType.PLATFORM_ACCESS },
    ];
  }, [t, tTemplates]);

  const statusOptions: StatusOption[] = useMemo(() => {
    if (!data) return [{ label: t("filters.allStatuses"), value: null }];
    const allDocsWithStatus = [
      ...data.vehicle_rentals,
      ...data.premises_leases,
      ...data.service_agreements_individual,
      ...data.service_agreements_msb,
      ...data.goods_supply,
      ...(data.platform_access || []),
      ...data.vehicle_handovers,
      ...data.premises_handovers,
    ];
    return [
      { label: t("filters.allStatuses"), value: null },
      ...getUniqueSigningStatuses(allDocsWithStatus, tCommon),
    ];
  }, [data, t, tCommon]);

  const allDocuments = useMemo(() => {
    if (!data) return [];

    const vehicleRentalActsCount = new Map<number, number>();
    data.vehicle_handovers.forEach((act) => {
      const count = vehicleRentalActsCount.get(act.parent_contract) || 0;
      vehicleRentalActsCount.set(act.parent_contract, count + 1);
    });

    const premisesLeaseActsCount = new Map<number, number>();
    data.premises_handovers.forEach((act) => {
      const count = premisesLeaseActsCount.get(act.parent_contract) || 0;
      premisesLeaseActsCount.set(act.parent_contract, count + 1);
    });

    return [
      ...data.vehicle_rentals.map((doc) => ({
        doc,
        type: LegalDocumentType.VEHICLE_RENTAL,
        actsCount: vehicleRentalActsCount.get(doc.id) || 0,
      })),
      ...data.premises_leases.map((doc) => ({
        doc,
        type: LegalDocumentType.PREMISES_LEASE,
        actsCount: premisesLeaseActsCount.get(doc.id) || 0,
      })),
      ...data.service_agreements_individual.map((doc) => ({
        doc,
        type: LegalDocumentType.SERVICE_AGREEMENT_INDIVIDUAL,
      })),
      ...data.service_agreements_msb.map((doc) => ({ doc, type: LegalDocumentType.SERVICE_AGREEMENT_MSB })),
      ...data.goods_supply.map((doc) => ({ doc, type: LegalDocumentType.GOODS_SUPPLY })),
      ...(data.platform_access || []).map((doc) => ({ doc, type: LegalDocumentType.PLATFORM_ACCESS })),
    ];
  }, [data]);

  const filteredDocuments = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();

    const result = allDocuments.filter((item) => {
      const matchesSearch = filterBySearch(item.doc, searchTerm);
      const matchesDocumentType = filterByType(item, documentTypeFilter);
      const matchesStatus = filterByStatus(item.doc, statusFilter);
      const matchesDate = filterByDate(item.doc.created_at, dateRange);

      return matchesSearch && matchesDocumentType && matchesStatus && matchesDate;
    });

    result.sort(sortDocumentsByDate);

    return result;
  }, [allDocuments, search, documentTypeFilter, statusFilter, dateRange]);

  const pagination = usePagination(filteredDocuments.length, 10, [
    search,
    dateRange,
    documentTypeFilter,
    statusFilter,
  ]);

  const pageDocuments = filteredDocuments.slice(pagination.startIndex, pagination.endIndex);

  const activeFilters = !!(search.trim() || dateRange.start || dateRange.end || documentTypeFilter || statusFilter);

  function handleResetFilters() {
    setSearch("");
    setDateRange({ start: null, end: null });
    setDocumentTypeFilter(null);
    setStatusFilter(null);
  }

  return {
    data,
    isLoading,
    isError,
    filteredDocuments,
    pagination,
    pageDocuments,
    search,
    setSearch,
    dateRange,
    setDateRange,
    documentTypeFilter,
    setDocumentTypeFilter,
    statusFilter,
    setStatusFilter,
    documentTypeOptions,
    statusOptions,
    activeFilters,
    handleResetFilters,
    locale,
    t,
    readOnly,
    signingRestricted,
  };
}
